/**
 * ServiceManagementPage Component (TASK-184 … TASK-197, split KAN-157)
 *
 * Read-only Services Tree Viewer.
 *  - N-level recursive service tree built from a flat list by `parent`
 *  - Type badge shows the REAL calculation method
 *    (Калькулятор / Прайс-лист / Классификатор / Справочник).
 *  - The aggregator role (is_aggregator OR has children) is shown as a
 *    SEPARATE «с подуслугами» marker next to the type badge, never as a type.
 *
 * TASK-197 — UNIFIED DETAIL CARD (contract revised by d2fa004).
 *  The detail panel (data-testid="service-detail-panel") renders, after the
 *  header (name <h3> + type badge + «с подуслугами» badge), the POPULATED
 *  subset of these 5 sections, always in this order, for every service type:
 *
 *    1. Входные параметры   data-testid="detail-section-inputs"
 *    2. Расчёт              data-testid="detail-section-calculation"
 *    3. Постобработка       data-testid="detail-section-postprocessing"
 *    4. Выходные данные     data-testid="detail-section-output"
 *    5. Подуслуги           data-testid="detail-section-subservices"
 *
 *  Each section heading is an <h4> (the service name stays in <h3>). An EMPTY
 *  section is NOT rendered at all: d2fa004 ("Пустые секции … скрываются вместо
 *  показа текста «пусто»") made DetailSection return null when empty, so the
 *  rendered set varies by service type. Emptiness is decided per section by an
 *  EXPLICIT boolean (not by inspecting React.Children).
 *
 *  «Расчёт» is the ONE exception: it is ALWAYS rendered (isEmpty={false} in
 *  ServiceDetailPanel), because for a leaf service (calculator/pricelist/
 *  classifier/questionnaire) having no calc data is ALWAYS an indexing defect,
 *  never a legitimate empty state (`.claude/rules/service-principles.md`).
 *  KAN-123 made this defect impossible to miss: when a leaf service has no
 *  chains/pricing_data/questionnaire_fields, its «Расчёт» body renders an
 *  explicit amber warning (`CalcEmptyNotice`, data-testid="<method>-empty",
 *  e.g. "calculator-empty"/"pricelist-empty"/"classifier-empty"/
 *  "questionnaire-empty") instead of a plain muted «Нет …» line. See KAN-123
 *  for the full rationale. The other 4 sections (Входные параметры/
 *  Постобработка/Выходные данные/Подуслуги) are unaffected by KAN-123 — their
 *  emptiness is often legitimate, so they stay fully hidden when empty, pinned
 *  by ServiceManagementPage.test.tsx › "TASK-197: unified detail card sections
 *  (empty sections hidden)".
 *
 *  Section content by service type:
 *    1. Входные параметры — client-facing inputs only:
 *         calculator → chain.required_inputs minus service-internal inputs
 *                      (*_from_child, результат_*, базовая_сумма, способ);
 *         pricelist  → per-position «количество '<name>' (<unit>)» lines
 *                      (data-testid="pricelist-inputs");
 *         classifier → modifier-chain user params (only if a chain exists).
 *    2. Расчёт — the main body:
 *         pricelist  → positions table (+ data-testid="pricelist-formula");
 *         classifier → variants table (ClassifierDetails);
 *         calculator → formula lines (extractFormulaLines) + constants.
 *    3. Постобработка — ModifierDetails (band-surcharge overlay, TASK-189),
 *         ONLY for pricelist/classifier WITH a chain; otherwise hidden.
 *    4. Выходные данные — output_name (+ unit) from the relevant chain.
 *    5. Подуслуги — SubservicesSection child navigation when there are children.
 *
 *  Earlier task contracts preserved inside the appropriate sections:
 *    - TASK-185: real calculation_method badge + «с подуслугами» marker.
 *    - TASK-189: pricelist/classifier keep their body AND show the modifier as
 *      a «Постобработка» overlay (modifier-postprocessing testid). Its inner
 *      heading is «Надбавки и коэффициенты» so the section's own «Постобработка»
 *      <h4> stays the single «Постобработка» text.
 *    - TASK-192: pricelist transparency blocks pricelist-inputs / pricelist-formula
 *      (rendered ONLY for the pricelist method).
 *  - No create / edit / delete / variant controls, no keyword clutter.
 *
 * KAN-157 — this file is now a THIN CONTAINER: state, data-fetching
 * (useQuery/useMutation) and event handlers only. Presentational pieces
 * (TreeNode, ServiceTypeLegend, ServiceDetailPanel and its sub-renderers)
 * live in `@/components/services/detail/`; pure tree/formula helpers live in
 * `@/utils/serviceTree.ts` and `@/utils/serviceFormula.ts`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Calculator, Loader2, Plus, RefreshCw, Wrench, X } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/api/client';
import { servicesApi } from '@/services/api/services';
import type { Service, TreeTestRequest, TreeTestResult, TreeTestSelection } from '@/services/api/services';
import { CreateServiceDialog } from '@/components/services/CreateServiceDialog';
import { DeleteServiceDialog } from '@/components/services/DeleteServiceDialog';
import { TreeTestResultPanel } from '@/components/services/TreeTestResultPanel';
import { ServiceDetailPanel } from '@/components/services/detail/ServiceDetailPanel';
import { ServiceTypeLegend } from '@/components/services/detail/ServiceTypeLegend';
import { TreeNode } from '@/components/services/detail/TreeNode';
import type { TreeTestCtx } from '@/components/services/detail/TreeNode';
import { useTreeTestStorage } from '@/hooks/useTreeTestStorage';
import { buildTree } from '@/utils/serviceTree';

export function ServiceManagementPage(): JSX.Element {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // CRUD state (TASK-215 Phase 2)
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [editService, setEditService] = useState<Service | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  // Manual tree-test mode (TASK-260)
  const [testMode, setTestMode] = useState(false);
  const [selections, setSelections] = useState<Map<number, TreeTestSelection>>(new Map());
  const [selectorChoices, setSelectorChoices] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<TreeTestResult | null>(null);

  const applyChangesMutation = useMutation({
    mutationFn: servicesApi.applyChanges,
    onSuccess: () => {
      toast.success('Изменения применены. Бот использует обновлённые услуги.');
    },
    onError: () => {
      toast.error('Ошибка при применении изменений');
    },
  });

  useEffect(() => {
    document.title = 'Услуги - SMETA AI';
  }, []);

  const {
    data: services,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApi.list,
  });

  const tree = useMemo(() => buildTree(services ?? []), [services]);

  const usedMethods = useMemo(
    () => [...new Set((services ?? []).map(s => s.calculation_method))],
    [services]
  );

  // KAN-233: split root services into a «questionnaire» group and a
  // «services» (calculable) group for the tree panel — visual split only,
  // buildTree()/childrenByParent traversal is unchanged for either group.
  const questionnaireRoots = useMemo(
    () => tree.roots.filter(r => r.calculation_method === 'questionnaire'),
    [tree.roots]
  );
  const otherRoots = useMemo(
    () => tree.roots.filter(r => r.calculation_method !== 'questionnaire'),
    [tree.roots]
  );

  // Auto-expand the whole tree once when services first arrive. We seed
  // `expanded` with every parent id (keys of childrenByParent = exactly the
  // nodes that have children, at any depth). A ref flag guards against
  // re-running on refetch/re-render so the user's manual collapse/expand
  // actions are never overwritten — handleToggle mutates `expanded` locally
  // and does not trigger a refetch, so the initial seed stays put.
  const didInitExpand = useRef(false);
  useEffect(() => {
    if (didInitExpand.current) return;
    if (!services || services.length === 0) return;
    didInitExpand.current = true;
    setExpanded(new Set(tree.childrenByParent.keys()));
  }, [services, tree]);

  const selectedService = useMemo(
    () => (selectedId !== null ? (services ?? []).find(s => s.id === selectedId) ?? null : null),
    [services, selectedId]
  );

  const handleToggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Manual tree-test mode (TASK-260) ────────────────────────────────────
  // Current user (for the per-user localStorage slot key).
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get<{ id: string }>('/api/v1/auth/me/');
      return res.data;
    },
  });
  const userId = currentUser?.id ?? '';
  const storage = useTreeTestStorage(userId);

  // Service ids on the path of every selected leaf — visual ancestor highlight.
  const highlightedIds = useMemo(() => {
    const set = new Set<number>();
    const byId = new Map<number, Service>((services ?? []).map(s => [s.id, s]));
    selections.forEach((_sel, id) => {
      let cur: Service | undefined = byId.get(id);
      const guard = new Set<number>();
      while (cur && !guard.has(cur.id)) {
        guard.add(cur.id);
        set.add(cur.id);
        cur = cur.parent !== null ? byId.get(cur.parent) : undefined;
      }
    });
    return set;
  }, [selections, services]);

  const buildTestRequest = (): TreeTestRequest => ({
    selections: Array.from(selections.values()),
    selector_choices: selectorChoices,
  });

  const testTreeMutation = useMutation({
    mutationFn: servicesApi.testTree,
    onSuccess: res => {
      setTestResult(res);
      // Persist the full run (request + result) into the user's single slot.
      storage.save({ request: buildTestRequest(), result: res });
    },
    onError: () => {
      toast.error('Ошибка расчёта дерева услуг');
    },
  });

  // Enter/exit test mode. On ENTER, hydrate from the user's saved slot
  // (dropping stale service_ids that no longer exist in the current tree).
  const handleToggleTestMode = () => {
    setTestMode(prev => {
      const next = !prev;
      if (next) {
        const currentIds = (services ?? []).map(s => s.id);
        const stored = storage.read(currentIds);
        if (stored) {
          const restored = new Map<number, TreeTestSelection>();
          stored.request.selections.forEach(sel => restored.set(sel.service_id, sel));
          setSelections(restored);
          setSelectorChoices(stored.request.selector_choices ?? {});
          setTestResult(stored.result);
        }
      }
      return next;
    });
  };

  const handleToggleLeaf = (service: Service, checked: boolean) => {
    setSelections(prev => {
      const next = new Map(prev);
      if (checked) {
        next.set(service.id, next.get(service.id) ?? { service_id: service.id });
      } else {
        next.delete(service.id);
      }
      return next;
    });
  };

  const handleSelectorChoice = (parentId: number, child: Service) => {
    // Record the chosen branch name for the SWITCH parent.
    setSelectorChoices(prev => ({ ...prev, [String(parentId)]: child.name }));
    // Replace any sibling selection with this branch (mutually exclusive).
    setSelections(prev => {
      const next = new Map(prev);
      const siblingIds = (services ?? []).filter(s => s.parent === parentId).map(s => s.id);
      siblingIds.forEach(id => next.delete(id));
      next.set(child.id, next.get(child.id) ?? { service_id: child.id });
      return next;
    });
  };

  const handlePatchSelection = (serviceId: number, patch: Partial<TreeTestSelection>) => {
    setSelections(prev => {
      const next = new Map(prev);
      const existing = next.get(serviceId) ?? { service_id: serviceId };
      next.set(serviceId, { ...existing, ...patch });
      return next;
    });
  };

  const handleResetTest = () => {
    storage.clear();
    setSelections(new Map());
    setSelectorChoices({});
    setTestResult(null);
  };

  const handleCalculate = () => {
    testTreeMutation.mutate(buildTestRequest());
  };

  const testCtx: TreeTestCtx = {
    selections,
    highlightedIds,
    onToggleLeaf: handleToggleLeaf,
    onSelectorChoice: handleSelectorChoice,
    onPatch: handlePatchSelection,
  };

  // Shared root renderer for both tree groups (KAN-233) — avoids duplicating
  // the <TreeNode> prop block in two JSX places.
  const renderRoot = (root: Service) => (
    <TreeNode
      key={root.id}
      service={root}
      childrenByParent={tree.childrenByParent}
      selectedId={selectedId}
      onSelect={s => setSelectedId(s.id)}
      expanded={expanded}
      onToggle={handleToggle}
      onAddChild={parentId => {
        setCreateParentId(parentId);
        setCreateDialogOpen(true);
      }}
      ancestors={new Set<number>()}
      depth={0}
      test={testMode ? testCtx : undefined}
    />
  );

  return (
    <PageLayout
      data-testid="service-management-page"
      title="Услуги"
      subtitle={
        <>
          Просмотр услуг организации <span className="hidden sm:inline">и их расчётной логики</span>
        </>
      }
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Wrench className="h-5 w-5 text-white" />
        </div>
      }
      titleAddon={
        <span
          data-testid="total-count"
          className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
          {services?.length ?? 0}
        </span>
      }
      breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Услуги' }]}
      actions={
        <div className="flex items-center gap-2">
          <Button
            data-testid="test-mode-button"
            variant={testMode ? 'default' : 'outline'}
            onClick={handleToggleTestMode}>
            {testMode ? <X className="h-4 w-4" /> : <Calculator className="h-4 w-4" />}
            <span className="hidden sm:inline ml-2">{testMode ? 'Выйти из теста' : 'Тестовый расчёт'}</span>
          </Button>
          <Button
            data-testid="apply-changes-button"
            onClick={() => applyChangesMutation.mutate()}
            disabled={applyChangesMutation.isPending}>
            {applyChangesMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="hidden sm:inline ml-2">Применить изменения</span>
          </Button>
        </div>
      }>
      {/* Loading state */}
      {isLoading && (
        <div data-testid="services-loading" className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Загрузка услуг...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div data-testid="services-error" className="flex items-center justify-center py-12 text-red-500">
          <AlertCircle className="h-6 w-6 mr-2" />
          <span>Ошибка загрузки услуг</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && services?.length === 0 && (
        <div
          data-testid="services-empty"
          className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Wrench className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">Нет услуг</p>
          <p className="text-sm mt-1">Услуги появятся после индексации источников данных</p>
        </div>
      )}

      {/* Tree + details */}
      {!isLoading && !error && services && services.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
          {/* Tree */}
          <div className="space-y-3 h-fit">
            <div
              data-testid="service-tree-droppable"
              className="rounded-lg border bg-white shadow-sm p-3 space-y-0.5">
              <ServiceTypeLegend methods={usedMethods} />
              {questionnaireRoots.length > 0 && (
                <div data-testid="tree-group-questionnaire" className="mb-2">
                  <div className="text-xs font-medium text-gray-500 px-2 py-1">Опросник</div>
                  {questionnaireRoots.map(renderRoot)}
                </div>
              )}
              {otherRoots.length > 0 && (
                <div data-testid="tree-group-services">
                  <div className="text-xs font-medium text-gray-500 px-2 py-1">Услуги</div>
                  {otherRoots.map(renderRoot)}
                </div>
              )}
            </div>
            <button
              data-testid="new-root-service-button"
              onClick={() => {
                setCreateParentId(null);
                setCreateDialogOpen(true);
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg border border-dashed border-gray-300 transition-colors">
              <Plus className="h-4 w-4" />
              Новая услуга
            </button>

            {/* Test-mode action bar (TASK-260) */}
            {testMode && (
              <div
                data-testid="test-mode-toolbar"
                className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
                <Button
                  data-testid="calculate-button"
                  size="sm"
                  onClick={handleCalculate}
                  disabled={selections.size === 0 || testTreeMutation.isPending}>
                  {testTreeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Calculator className="h-4 w-4" />
                  )}
                  <span className="ml-2">Рассчитать</span>
                </Button>
                <Button data-testid="reset-test-button" size="sm" variant="outline" onClick={handleResetTest}>
                  <X className="h-4 w-4" />
                  <span className="ml-2">Сбросить</span>
                </Button>
                <span className="ml-auto text-xs text-gray-500">Выбрано: {selections.size}</span>
              </div>
            )}
          </div>

          {/* Details / Test result */}
          <div>
            {testMode ? (
              <TreeTestResultPanel result={testResult} />
            ) : selectedService ? (
              <ServiceDetailPanel
                service={selectedService}
                childrenByParent={tree.childrenByParent}
                onSelect={s => setSelectedId(s.id)}
                onEdit={s => setEditService(s)}
                onDelete={s => {
                  setServiceToDelete(s);
                  setDeleteDialogOpen(true);
                }}
              />
            ) : (
              <div className="rounded-lg border border-dashed bg-white/50 p-8 text-center text-gray-400">
                Выберите услугу в дереве слева, чтобы увидеть детали
              </div>
            )}
          </div>
        </div>
      )}
      {/* CRUD Dialogs (TASK-215 Phase 2) */}
      <CreateServiceDialog
        open={createDialogOpen || editService !== null}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditService(null);
        }}
        parentId={createParentId}
        service={editService ?? undefined}
      />
      <DeleteServiceDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setServiceToDelete(null);
        }}
        service={serviceToDelete}
        onDeleted={() => {
          setDeleteDialogOpen(false);
          setServiceToDelete(null);
          setSelectedId(null);
        }}
      />
    </PageLayout>
  );
}

export default ServiceManagementPage;
